import { useEffect, useState } from "react";
import { connections, type SecretStatus } from "./api";
import { SecretEditor } from "./SecretEditor";
import type { SourceResult } from "../lib/api";
import { chatHref, connectionHref, conversationHref, WORKSPACE_HREF } from "./navigation";
import { useConversationsDrawn } from "../lib/conversations";
import { settingsTitle, type SettingsDestination } from "./directory";

/**
 * 「A credential is waiting to be typed」, on the panel that asked for it.
 *
 * Which of a panel's reads answers that is the panel's own word:
 * `metadata.secure_inputs.index`. This file used to find it by the read's tool
 * name, which meant the shell knew which plugin collects credentials and, one
 * link later, which panel to send somebody back to. It knows neither now: the
 * way back is this panel's own key, carried in the link it builds.
 *
 * And what one row of that read *is* is the panel's word too. Five column names
 * and two status values used to be written here -- `request_id`, `plugin`,
 * `status`, `session_id`, `allowlist`, and the words `pending` and `submitted`.
 * That is one plugin's data shape living in the shell: rename a column there
 * and this draws nothing, silently, with no declaration anywhere saying the two
 * were connected. Now `secure_inputs.fields` and `secure_inputs.states` say it,
 * a panel that does not say it has no waiting credentials as far as this is
 * concerned, and there is no second guess (§7.53, §7.183).
 *
 * The sentences stay the shell's own, because the affordance is the shell's:
 * a secure form that never puts a value in a tool's arguments is something this
 * console offers, not something a plugin drew.
 */
export function SecureInputLinks({ reads, panelKey, declared, destinations = [], onInputFailed, onInputStored }: {
  reads: SourceResult[];
  onInputFailed?: () => void;
  onInputStored?: () => void;
  /** The panel these reads belong to, and the way back from 设置. */
  panelKey: string;
  /** That panel's `metadata`, where it says which read is the request list. */
  declared?: Record<string, unknown> | null;
  /** For the name of the setting each request points at. */
  destinations?: SettingsDestination[];
}) {
  const marker = object(declared?.secure_inputs);
  const shape = rowShape(marker);
  const index = marker.index;
  const source = typeof index === "number" ? reads[index] : undefined;
  if (!shape || !source || source.state !== "ready") return null;
  const value = source.value as { requests?: unknown[] } | undefined;
  const only = text(marker.request_id);
  const requests = (Array.isArray(value?.requests) ? value.requests : []).filter(
    (row): row is Record<string, unknown> => Boolean(row && typeof row === "object"),
  ).filter(row => typeof row[shape.id] === "string" && typeof row[shape.namespace] === "string"
    && (row[shape.status] === shape.waiting || row[shape.status] === shape.done))
    .filter(row => !only || row[shape.id] === only);
  if (!requests.length) return null;
  return <Requests requests={requests} shape={shape} inline={marker.inline === true} panelKey={panelKey}
    destinations={destinations} onInputFailed={onInputFailed} onInputStored={onInputStored} />;
}

function Requests({ requests, shape, inline, panelKey, destinations, onInputFailed, onInputStored }: {
  requests: Record<string, unknown>[]; shape: RowShape;
  /** The boxes are right here when the panel says `inline`, so there is nowhere to be sent to fill them,
   *  and over the conversation there is nowhere to go back to: the Agent goes on by itself once it is answered. */
  inline: boolean; panelKey: string; destinations: SettingsDestination[];
  onInputFailed?: () => void; onInputStored?: () => void;
}) {
  const sessionOf = (row: Record<string, unknown>) => shape.session && typeof row[shape.session] === "string" && row[shape.session]
    ? String(row[shape.session]) : undefined;
  // Whether the owner can read the conversation each request came from: the way back leads there only if so.
  const drawn = useConversationsDrawn(requests.map(sessionOf));
  const cutOff = requests.every(row => { const session = sessionOf(row); return session !== undefined && drawn[session] === false; });
  return <section className="connection-secrets" aria-label="所需凭据">
    {inline ? <p className="connection-secrets-lead">填好后，Agent 接着做。</p> : <>
      <h2>补上所需凭据</h2>
      <p>{cutOff ? "在安全表单里填写，填好后 Agent 接着做。" : "在安全表单里填写，填好后回到对话继续。"}</p>
    </>}
    {requests.map(row => {
      const id = String(row[shape.id]);
      const namespace = String(row[shape.namespace]);
      const waiting = row[shape.status] === shape.waiting;
      const session = sessionOf(row);
      const here = session ? drawn[session] : undefined;
      return <article key={id} className="connection-secret">
        <strong>{settingsTitle(destinations, namespace, namespace)}</strong>
        <div className="connection-quick-link">
          {inline && waiting
            ? <RequestSecrets namespace={namespace} names={secretNames(row, shape)} onFailed={onInputFailed} onStored={onInputStored} />
            : waiting
              ? <a className="btn" href={`${connectionHref(namespace, session, session ? conversationHref(session, here) : "")}&${new URLSearchParams({ request_id: id, request_panel: panelKey })}`}>填写所需凭据</a>
              : <span>已安全填写</span>}
          {inline || !session || here === undefined ? null
            : here ? <a className="btn" href={chatHref(session)}>回到对话</a>
              : <a className="btn" href={WORKSPACE_HREF}>返回工作区</a>}
        </div>
      </article>;
    })}
  </section>;
}

/** What one row of the request list is called, as the panel declared it. */
interface RowShape {
  id: string;
  namespace: string;
  status: string;
  /** May be empty: a request need not have come out of a conversation. */
  session: string;
  secrets: string;
  waiting: string;
  /** May be empty: a list that never keeps a finished row does not need one. */
  done: string;
}

/**
 * The declared shape, or null when the panel did not declare one.
 *
 * Null is drawn as "no waiting credentials". Guessing instead is what this
 * whole file stopped doing: a shell that falls back to names it remembers is a
 * shell that still knows them.
 */
function rowShape(marker: Record<string, unknown>): RowShape | null {
  const fields = object(marker.fields);
  const states = object(marker.states);
  const shape = {
    id: text(fields.id), namespace: text(fields.namespace), status: text(fields.status),
    session: text(fields.session), secrets: text(fields.secrets),
    waiting: text(states.waiting), done: text(states.done),
  };
  return shape.id && shape.namespace && shape.status && shape.secrets && shape.waiting ? shape : null;
}

/** The credentials this request is asking for, named by the row itself. */
function secretNames(row: Record<string, unknown>, shape: RowShape): string[] {
  const asked = row[shape.secrets];
  return Array.isArray(asked) ? asked.map(String) : [];
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function RequestSecrets({ namespace, names, onFailed, onStored }: {
  namespace: string;
  names: string[];
  onFailed?: () => void;
  onStored?: () => void;
}) {
  const [secrets, setSecrets] = useState<SecretStatus[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let live = true;
    connections.one(namespace).then(result => { if (live) { setSecrets(result.secrets); setFailed(false); } })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [namespace, revision]);
  const stored = async () => {
    try {
      const result = await connections.one(namespace);
      setSecrets(result.secrets);
      if (names.length && names.every(name => result.secrets?.some(field => field.name === name && field.present))) onStored?.();
    } catch { setFailed(true); }
  };
  if (failed) return <p role="alert">暂时无法确认凭据状态。<button className="btn" onClick={() => setRevision(n => n + 1)}>重试</button></p>;
  if (!secrets) return <p role="status">正在确认所需凭据…</p>;
  return <div>{names.map(name => {
    const field = secrets.find(secret => secret.name === name || Boolean(secret.prefix && name.startsWith(secret.prefix)));
    return field ? <SecretEditor key={name} namespace={namespace} name={name}
      label={field.title || name} description={field.description} present={field.present}
      onFailed={onFailed} onStored={() => { void stored(); }} /> : <p key={name} role="alert">这项凭据已经不需要了，请回对话重新确认。</p>;
  })}</div>;
}
